import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { Search, ChevronDown, ChevronRight, BookOpen, Target, FileText } from 'lucide-react';
import { StandarSPMI, IndikatorSPMI } from '../../types';

interface SpmiDictionaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  standarList: StandarSPMI[];
  indikatorList: IndikatorSPMI[];
  selectedStandarId?: number | null;
  selectedIndikatorId?: number | null;
  onSelectIndikator: (selection: { standarId: number; indikatorId: number }) => void;
}

const SpmiDictionaryModal: React.FC<SpmiDictionaryModalProps> = ({
  isOpen,
  onClose,
  standarList,
  indikatorList,
  selectedStandarId = null,
  selectedIndikatorId = null,
  onSelectIndikator,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStandar, setExpandedStandar] = useState<number[]>([]);
  const [collapsedSearchResults, setCollapsedSearchResults] = useState<number[]>([]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setCollapsedSearchResults([]);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setExpandedStandar([]);
      setCollapsedSearchResults([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const isSearchActive = normalizedSearchTerm !== '';

  const normalizeText = (value?: string | null) => String(value || '').toLowerCase();

  const indikatorByStandar = indikatorList.reduce<Record<number, IndikatorSPMI[]>>((acc, indikator) => {
    if (!acc[indikator.standar_spmi_id]) {
      acc[indikator.standar_spmi_id] = [];
    }

    acc[indikator.standar_spmi_id].push(indikator);
    return acc;
  }, {});

  const toggleExpand = (id: number) => {
    if (isSearchActive) {
      setCollapsedSearchResults((prev) =>
        prev.includes(id) ? prev.filter((standarId) => standarId !== id) : [...prev, id]
      );
      return;
    }

    setExpandedStandar((prev) =>
      prev.includes(id) ? prev.filter((standarId) => standarId !== id) : [...prev, id]
    );
  };

  const filteredStandar = standarList
    .map((standar) => {
      const relatedIndikators = indikatorByStandar[standar.id] || [];
      const matchStandar = !isSearchActive
        || normalizeText(standar.nama).includes(normalizedSearchTerm)
        || normalizeText(standar.kode).includes(normalizedSearchTerm);
      const matchingIndikators = isSearchActive
        ? relatedIndikators.filter((indikator) =>
            normalizeText(indikator.nama).includes(normalizedSearchTerm)
            || normalizeText(indikator.kode).includes(normalizedSearchTerm)
          )
        : relatedIndikators;

      return {
        standar,
        relatedIndikators,
        displayedIndikators: matchStandar ? relatedIndikators : matchingIndikators,
        matchStandar,
      };
    })
    .filter((item) => !isSearchActive || item.matchStandar || item.displayedIndikators.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Kamus Standar & Indikator SPMI"
      size="xl"
    >
      <div className="flex flex-col h-[70vh] -mt-2 -mx-2">
        <div className="px-2 pb-4 border-b border-neutral-border">
          <p className="text-sm text-neutral-text-secondary mb-4">
            Gunakan kamus ini untuk memahami setiap standar dan indikator yang digunakan dalam Sistem Penjaminan Mutu Internal (SPMI).
          </p>
          <p className="text-xs text-teal-dark/80 mb-4">
            Klik indikator yang relevan untuk langsung mengisi referensi SPMI ke formulir laporan.
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
            <input
              type="text"
              placeholder="Cari kata kunci standar, kode, atau indikator..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              className="w-full pl-10 pr-4 py-2 border border-neutral-border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-main/20 text-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-3 mt-4">
          {filteredStandar.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-neutral-text-secondary">
              <BookOpen className="w-12 h-12 mb-3 opacity-20" />
              <p>Tidak ada Standar atau Indikator yang cocok dengan pencarian.</p>
            </div>
          ) : (
            filteredStandar.map(({ standar, relatedIndikators, displayedIndikators }) => {
              const isStandarSelected = selectedStandarId === standar.id;
              const isExpanded = isSearchActive
                ? !collapsedSearchResults.includes(standar.id)
                : expandedStandar.includes(standar.id);

              return (
                <div
                  key={standar.id}
                  className={`border rounded-xl overflow-hidden bg-white transition-colors ${
                    isStandarSelected ? 'border-teal-200 shadow-sm shadow-teal-main/10' : 'border-neutral-border'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleExpand(standar.id)}
                    className={`w-full flex items-center justify-between p-4 transition-colors text-left ${
                      isStandarSelected ? 'bg-teal-50/70 hover:bg-teal-50' : 'bg-neutral-bg hover:bg-neutral-bg/80'
                    }`}
                  >
                    <div className="flex gap-3 items-start">
                      <div className={`p-2 rounded-lg shrink-0 ${isStandarSelected ? 'bg-teal-main/15 text-teal-dark' : 'bg-teal-main/10 text-teal-dark'}`}>
                        <BookOpen className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-bold text-neutral-text-primary text-sm">
                          {standar.kode} - {standar.nama}
                        </div>
                        {standar.deskripsi ? (
                          <div className="text-xs text-neutral-text-secondary mt-1">{standar.deskripsi}</div>
                        ) : (
                          <div className="text-xs text-neutral-text-secondary italic mt-1">Tidak ada deskripsi</div>
                        )}
                        <div className="text-[10px] text-teal-dark mt-2 font-semibold">
                          {relatedIndikators.length} Indikator Terkait
                        </div>
                        {isStandarSelected && (
                          <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full bg-teal-main/10 text-[10px] font-semibold text-teal-dark">
                            Standar terpilih
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 ml-4 text-neutral-text-secondary">
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="p-4 border-t border-neutral-border bg-white space-y-3">
                      {displayedIndikators.length === 0 ? (
                        <p className="text-xs text-neutral-text-secondary italic pl-11">
                          Belum ada indikator untuk standar ini.
                        </p>
                      ) : (
                        displayedIndikators.map((indikator) => {
                          const isIndikatorSelected = selectedIndikatorId === indikator.id;

                          return (
                            <button
                              key={indikator.id}
                              type="button"
                              onClick={() => {
                                onSelectIndikator({ standarId: standar.id, indikatorId: indikator.id });
                                onClose();
                              }}
                              className={`w-full flex gap-3 items-start pl-11 pr-3 py-3 rounded-xl border text-left transition-all ${
                                isIndikatorSelected
                                  ? 'border-teal-300 bg-teal-50 shadow-sm shadow-teal-main/10'
                                  : 'border-transparent hover:border-teal-200 hover:bg-neutral-bg'
                              }`}
                            >
                              <div className={`shrink-0 mt-0.5 ${isIndikatorSelected ? 'text-teal-main' : 'text-success'}`}>
                                <Target className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="text-sm font-semibold text-neutral-text-primary">
                                    {indikator.kode} - {indikator.nama}
                                  </div>
                                  {isIndikatorSelected && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-main text-[10px] font-semibold text-white">
                                      Sedang dipakai
                                    </span>
                                  )}
                                </div>
                                {indikator.deskripsi && (
                                  <p className="text-xs text-neutral-text-secondary mt-1 max-w-[90%]">
                                    <FileText className="inline w-3 h-3 mr-1" />
                                    {indikator.deskripsi}
                                  </p>
                                )}
                                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                  <div className="inline-block px-2 py-0.5 bg-neutral-100 border border-neutral-200 rounded text-[10px] font-medium text-neutral-text-secondary">
                                    Target: {indikator.target_value || 'Tidak spesifik'}
                                  </div>
                                  <span className="text-[10px] font-semibold text-teal-dark">
                                    Klik untuk gunakan indikator ini
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SpmiDictionaryModal;
